import cn from "classnames";

import { ImgBgResponsive } from "@/src/component/base/ImgBgResponsive";
import { VideoPlayer } from "@/src/component/partial/VideoPlayer";
import CMSLogic from "@/src/logic/CMSLogic";
import { ComponentType, StrapiComponentType, StrapiType } from "@/src/type";
import { LoadFromCDN } from "@/src/util";

import { SectionModule, TextBreakpointModule, ButtonLinkModule, ImageBreakpointModule } from "../component";

import styles from "./SideBySide.module.scss";

interface SideBySideProps extends ComponentType.DynamicPageSectionComponentProps<StrapiComponentType.Section.SideBySide> {}

function SideBySide({ section, slug, snapToGrid, lazy, classes, searchParams, mobileHint, quality, layout }: SideBySideProps) {
  const videoId = `${slug}.${section.sectionId}.sideBySide.video`;
  const sideBySide: StrapiComponentType.Section.SideBySide = section.type?.[0];

  /**
   * determine the horizontal position of the text box
   */
  function getTextAlign() {
    switch (sideBySide.textAlign ?? "left") {
      case "center":
        return styles.center;
      case "right":
        return styles.right;
      default:
        return styles.left;
    }
  }

  /**
   * determine the vertical position of the text box
   */
  function getTextPosition() {
    switch (sideBySide.textPosition ?? "middle") {
      case "top":
        return styles.top;
      case "bottom":
        return styles.bottom;
      default:
        return styles.middle;
    }
  }

  /**
   * determine if an image should be used
   * when video, use the background image until the video starts playing
   */
  function getImageAssetWhenAvailable(): StrapiType.StrapiAsset | undefined {
    return CMSLogic.isImage(sideBySide?.image?.primary)
      ? sideBySide?.image?.primary
      : CMSLogic.isImage(sideBySide?.image?.background)
        ? sideBySide?.image?.background
        : undefined;
  }

  return (
    <SectionModule
      section={section}
      slug={slug}
      stylePrefix="SideBySide"
      styleContainerId={sideBySide.id}
      snapToGrid={snapToGrid}
      classes={{
        section: classes?.section,
      }}
      searchParams={searchParams}
      mobileHint={mobileHint}
      quality={quality}
      layout={layout}
      lazy={lazy}
    >
      <div
        className={cn(sideBySide.directionLTR ? styles.ltr : styles.rtl, sideBySide.directionDownMobile ? styles.down : undefined, styles.container)}
      >
        <div className={cn(styles.imgContainer, snapToGrid ? styles.snapToGrid : undefined)}>
          {CMSLogic.isVideo(sideBySide?.image?.primary) && (
            <VideoPlayer
              lazy={lazy ?? "lazy"}
              asset={sideBySide?.image?.primary}
              videoId={videoId}
              classes={{
                container: styles.elevation,
                video: styles.video,
              }}
            />
          )}
          {getImageAssetWhenAvailable() && (
            <ImgBgResponsive
              lazy={lazy ?? "lazy"}
              imageUrl={LoadFromCDN.imageSizeAndQuality(getImageAssetWhenAvailable()?.data?.attributes?.url, 100, 1920)}
              className={styles.sideImage}
              prefetch={lazy === "eager"}
            />
          )}
          {sideBySide.breakpointImages && (
            <ImageBreakpointModule
              images={sideBySide.breakpointImages}
              preload={true}
              classes={
                {
                  //container: styles.breakpointImage,
                }
              }
              id={sideBySide.id}
              mobileHint={mobileHint}
              quality={quality}
              lazy={lazy}
            />
          )}
        </div>
        <div className={cn(styles.textContainer, getTextPosition(), getTextAlign())}>
          <TextBreakpointModule
            id={`sideBySide_title_${sideBySide.id}`}
            text={section.title}
            className={styles.title}
          />
          {sideBySide?.facts?.text && (
            <TextBreakpointModule
              id={`sideBySide_facts_${sideBySide.id}`}
              text={sideBySide.facts}
              className={styles.facts}
            />
          )}
          {sideBySide?.description?.text && (
            <TextBreakpointModule
              id={`sideBySide_desc_${sideBySide.id}`}
              text={sideBySide.description}
              className={styles.description}
            />
          )}
          {sideBySide.cta && (
            <div className={styles.buttons}>
              {(sideBySide.cta ?? []).map((cta, i) => (
                <ButtonLinkModule
                  key={i}
                  buttonLink={cta}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </SectionModule>
  );
}

export default SideBySide;
